/* Iter250 · OCPA Enrich Button + Paste Modal
   ---------------------------------------------------------------
   Reads the prospect's property.address + city + state and:
     1. If APIFY is configured → POSTs /smith/ocpa/enrich/{pid}
        (residential-proxy scraper).
     2. If not, opens a modal guiding the user to open the OCPA page
        in a new tab, copy the parcel JSON, paste it here, and hit
        Ingest — which POSTs /smith/ocpa/ingest/{pid}.
*/
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building, Loader2, ExternalLink, X, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OCPA_URL = "https://ocpaweb.ocpafl.org/parcelsearch";

export function OcpaEnrichButton({ prospectId, address, state, onEnriched,
                                     testid = "ocpa-enrich-btn" }) {
  const [loading, setLoading] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);

  const canEnrich = state === "FL" && !!address;

  const runEnrich = async () => {
    if (!canEnrich) {
      toast.error("OCPA cobre apenas Flórida — este lead não é elegível");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/ocpa/enrich/${prospectId}`, {}, { timeout: 60000 });
      if (data.ok) {
        const added = Object.keys(data.fields_added || {});
        const src = data.county
          ? `${data.county_pa_name || data.county + " PA"}`
          : "OCPA";
        if (added.length) {
          toast.success(`${src} enriqueceu ${added.length} campos (${added.join(", ")})`);
        } else {
          toast.info(`${src} respondeu, mas prospect já tinha os campos preenchidos`);
        }
        onEnriched?.(data);
      } else if (data.county_pa_url) {
        // Iter257 · Multi-county fallback — scrape failed OR county
        // has no Apify support yet. Open the county PA search in a
        // new tab so the operator never hits a dead end.
        const reason = data.status === "no_apify"
          ? "Apify não configurado"
          : data.status === "timeout"
            ? "Scraper demorou demais"
            : data.status === "not_found"
              ? "Endereço não encontrado no scrape"
              : `Scrape falhou (${data.status})`;
        toast.info(`${reason} — abrindo ${data.county_pa_name} em nova aba`, {
          duration: 8000,
          action: {
            label: "Abrir de novo",
            onClick: () => window.open(data.county_pa_url, "_blank", "noopener"),
          },
        });
        window.open(data.county_pa_url, "_blank", "noopener");
      } else if (data.status === "no_apify") {
        toast.warning("APIFY não configurado — abrindo modo Paste");
        setPasteMode(true);
      } else if (data.status === "cache_hit") {
        toast.success("OCPA respondeu via cache");
        onEnriched?.(data);
      } else if (data.status === "not_found") {
        toast.error("Endereço não encontrado no OCPA");
      } else {
        toast.error(`OCPA falhou: ${data.status}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao chamar OCPA");
    } finally { setLoading(false); }
  };

  return (
    <>
      <button type="button" onClick={runEnrich} disabled={loading || !canEnrich}
              data-testid={testid}
              title={canEnrich ? "Enriquecer com Orange County Property Appraiser"
                                   : "OCPA cobre apenas Flórida"}
              className="h-8 px-2.5 rounded-lg font-bold text-[11px] text-white inline-flex items-center gap-1 disabled:opacity-40"
              style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
        {loading
          ? <><Loader2 className="w-3 h-3 animate-spin" /> OCPA…</>
          : <><Building className="w-3 h-3" /> Enrich OCPA</>}
      </button>
      {pasteMode && (
        <OcpaPasteModal
          prospectId={prospectId}
          address={address}
          onClose={() => setPasteMode(false)}
          onIngested={(d) => { setPasteMode(false); onEnriched?.(d); }} />
      )}
    </>
  );
}


function OcpaPasteModal({ prospectId, address, onClose, onIngested }) {
  const [pastedText, setPastedText] = useState("");
  const [busy, setBusy] = useState(false);

  const parseAndIngest = async () => {
    if (!pastedText.trim()) {
      toast.error("Cole o conteúdo copiado do OCPA primeiro");
      return;
    }
    // Best-effort parse: find "Label: value" pairs the operator copied.
    const fields = {};
    const patterns = {
      "Property Address": "address",
      "Parcel ID": "parcel_id",
      "Total Building Area": "sqft_building",
      "Building Area": "sqft_building",
      "Total Land Area": "sqft_lot",
      "Land Area": "sqft_lot",
      "Year Built": "year_built",
      "Property Use": "property_use",
      "Zoning": "zoning",
      "Owner": "owner_name",
      "Just Market Value": "assessed_value",
      "Total Assessed Value": "assessed_value",
      "Subdivision": "subdivision",
    };
    for (const [label, canon] of Object.entries(patterns)) {
      const rx = new RegExp(`${label}\\s*[:\\-]?\\s*([^\\n\\r]{1,80})`, "i");
      const m = pastedText.match(rx);
      if (m) {
        let v = m[1].trim();
        if (["sqft_building", "sqft_lot", "year_built", "assessed_value"].includes(canon)) {
          const digits = v.replace(/[^\d]/g, "");
          if (digits) fields[canon] = parseInt(digits, 10);
        } else {
          fields[canon] = v;
        }
      }
    }
    if (Object.keys(fields).length === 0) {
      toast.error("Não consegui extrair campos. Verifique se colou o texto do parcel completo.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/ocpa/ingest/${prospectId}`,
        { address, fields },
        { timeout: 20000 });
      const added = Object.keys(data.fields_added || {});
      toast.success(`Ingest OK — ${added.length} campos adicionados`);
      onIngested?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no ingest");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="ocpa-paste-modal"
         onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-cyan-500/30 bg-slate-900 shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           style={{ background: "linear-gradient(135deg,rgba(14,165,233,0.12) 0%,rgba(15,23,42,0.95) 60%)" }}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-cyan-400" />
            <h3 className="font-extrabold text-white text-[13.5px] uppercase tracking-wider">
              OCPA · Ingest via Browser
            </h3>
          </div>
          <button type="button" onClick={onClose} data-testid="ocpa-paste-close"
                  className="h-8 w-8 rounded-lg border border-white/15 text-white/70 hover:text-white inline-flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-[11.5px] text-amber-100">
            OCPA implementa firewall anti-bot que bloqueia nosso servidor.
            <b> Como contornar em 3 passos:</b>
            <ol className="list-decimal ml-5 mt-1 space-y-0.5">
              <li>Clique no botão abaixo para abrir OCPA em nova aba</li>
              <li>Pesquise o endereço, clique no parcel e <b>copie a página inteira</b> (Ctrl+A → Ctrl+C)</li>
              <li>Cole aqui embaixo e clique <b>Ingerir</b></li>
            </ol>
          </div>

          <a href={`${OCPA_URL}?address=${encodeURIComponent(address || "")}`}
             target="_blank" rel="noopener noreferrer"
             data-testid="ocpa-open-external"
             className="inline-flex items-center gap-1.5 text-[12.5px] px-3 py-2 rounded-lg bg-cyan-500/25 text-cyan-100 hover:bg-cyan-500/40 font-bold">
            <ExternalLink className="w-3.5 h-3.5" />
            Abrir OCPA em nova aba
          </a>

          <textarea
            data-testid="ocpa-paste-textarea"
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder="Cole aqui o texto do parcel completo (Ctrl+A → Ctrl+C na página do OCPA)…"
            className="w-full h-40 rounded-lg bg-black/60 border border-white/15 text-white text-[12px] px-3 py-2 font-mono placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-400/60" />

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose}
                    className="h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 border border-white/15 hover:bg-white/5">
              Cancelar
            </button>
            <button type="button" onClick={parseAndIngest} disabled={busy}
                    data-testid="ocpa-paste-ingest-btn"
                    className="h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Ingerir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
